import * as React from 'react';
import { useWindowSize } from 'react-use';
import ResizeObserver from 'resize-observer-polyfill';

import { Footer } from 'components/Footer';
import { LanguageSelector } from 'components/LanguageSelector';
import { Logo } from 'components/Logo';
import {
  Content,
  LoginWrapper,
  NoticesContent,
  NoticesWrapper,
  PageWrapper,
  Triangle,
  TriangleWrapper
} from 'components/MainLayout/styled';
import { SupportOptions } from 'components/SupportOptions';
import { AppNotices } from 'components/appNotices';
import { StatusPageIssues } from 'components/statusPage/StatusPageIssues';
import { useActiveIncidentsAndAppNotices } from 'components/useActiveIncidentsAndAppNotices';
import { BREAKPOINTS } from 'utils/constants';

export function MainLayout({
  children
}: {
  children?: React.ReactNode;
}): JSX.Element {
  const { width } = useWindowSize();
  const isLaptop = width <= parseInt(BREAKPOINTS.laptop, 10);
  const { isLoading, activeIncidents, appNotices } =
    useActiveIncidentsAndAppNotices();
  const hasActiveIncidentsOrAppNotices =
    activeIncidents.length !== 0 || appNotices.length !== 0;
  const shouldDisplayIncidentsOrAppNotices =
    !isLaptop && hasActiveIncidentsOrAppNotices;
  const [triangleHeight, setTriangleHeight] = React.useState(0);
  const supportOptionsRef = React.useRef<HTMLDivElement>(null);

  return (
    <PageWrapper>
      <LoginWrapper
        ref={(element) => {
          const resizeObserver = new ResizeObserver(
            (items: ResizeObserverEntry[]) => {
              items.forEach(({ target }) => {
                if (supportOptionsRef.current) {
                  setTriangleHeight(
                    target.scrollTop +
                      supportOptionsRef.current.getBoundingClientRect().top
                  );
                }
              });
            }
          );
          if (element) resizeObserver.observe(element);
        }}
      >
        <TriangleWrapper
          className="animated slideInDown fast"
          style={
            {
              '--height': triangleHeight ? `${triangleHeight}px` : '85vh'
            } as React.CSSProperties
          }
        >
          <Triangle />
        </TriangleWrapper>
        <Logo />
        {isLoading || !shouldDisplayIncidentsOrAppNotices ? (
          <LanguageSelector />
        ) : null}
        <Content>
          {children}
          {isLaptop ? (
            <>
              <StatusPageIssues
                incidents={activeIncidents}
                shouldUseAnimation
              />
              <AppNotices
                appNotices={appNotices}
                shouldUseCardStyles
                shouldUseAnimation
              />
            </>
          ) : null}
          <SupportOptions supportOptionsRef={supportOptionsRef} />
          <Footer />
        </Content>
      </LoginWrapper>
      {!isLaptop ? (
        <NoticesWrapper
          className={`animated ${
            !isLoading && shouldDisplayIncidentsOrAppNotices ? 'showUpLeft' : ''
          } faster`}
        >
          <LanguageSelector isDark />
          <NoticesContent>
            <StatusPageIssues incidents={activeIncidents} />
            <AppNotices appNotices={appNotices} />
          </NoticesContent>
        </NoticesWrapper>
      ) : null}
    </PageWrapper>
  );
}
